import type {AppDomainContext} from "app/services/domain/domain.module";
import {TagBaselineDefinitionExtended} from "app/services/domain/tag-baseline-definitions.service";
import {extractDefinedTags} from "app/services/domain/tag-baseline-versions.service";
import * as Models from "app/services/proxy/model/models";
import {PluralizedValues, UtilsService} from "framework/services/utils.service";
import {KeyValueChip} from "framework/ui/chips/key-value-chip-list.component";
import {DatatableSort} from "framework/ui/datatables/datatable-manager";
import {mapInParallelNoNulls} from "framework/utils/concurrency";

export class TagBaselineScopeChip
{
    public readonly keyValueChips: KeyValueChip[];

    constructor(public readonly type: string,
                public readonly details: string,
                public readonly list?: string[])
    {
        this.keyValueChips = [new KeyValueChip(type, details, list)];
    }

    static async create(scope: Models.TagBaselineScope,
                        domain: AppDomainContext)
    {
        let filteroptions = await domain.filterOptions.getFilterOptions();
        if (scope instanceof Models.TagBaselineScopeGlobal)
        {
            // TODO: Do we have a way to look up Org name?
            return new TagBaselineScopeChip("", "Global");
        }
        else if (scope instanceof Models.TagBaselineScopeTenant)
        {
            const tenants               = scope.tenant_ids;
            const tenantNames: string[] = [];

            for (let tenantId of tenants)
            {
                const hasActiveCloudAccounts = await this.isCloudAccountsAvailable(tenantId, domain);

                if (hasActiveCloudAccounts)
                {
                    const tenantName = domain.users.resolveTenantId(tenantId);
                    if (tenantName)
                    {
                        tenantNames.push(tenantName);
                    }
                }
            }
            const details = tenantNames.length > 1 ? `+${tenantNames.length}` : tenantNames[0];
            return new TagBaselineScopeChip("Tenant", details, tenantNames);
        }
        else if (scope instanceof Models.TagBaselineScopeCloudAccount)
        {
            const accounts     = scope.cloud_account_ids;
            const accountNames = accounts.filter((id) => filteroptions.getStatusFromCloudAccount(id) === Models.ServiceAccountStatus.active)
                                         .map((id) => filteroptions.resolveAccountId(id))
                                         .filter((name) => !!name);
            const details      = accountNames.length > 1 ? `+${accountNames.length}` : accountNames[0];
            return new TagBaselineScopeChip("Account", details, accountNames);
        }

        return null;
    }

    static async isCloudAccountsAvailable(tenantId: string,
                                          domain: AppDomainContext): Promise<boolean>
    {
        const filterData = Models.ServiceAccountFilter.newInstance({
                                                                       only_active_tenants: true,
                                                                       tenant_ids         : [tenantId],
                                                                       statuses           : [
                                                                           Models.ServiceAccountStatus.active
                                                                       ]
                                                                   });

        const request = Models.ServiceAccountListRequest.newInstance({
                                                                         filters: filterData
                                                                     });

        const activeAccounts = await domain.accountGovernanceList.list(request);
        return !!activeAccounts.results.length;
    }
}

export enum TagType
{
    Custom,
    CorestackBaseline,
    AvailableTag,
    Inherited,
    AIEnhanced,
    AIGenerated
}

export class TagRule
{
    constructor(public readonly type: TagType,
                public key: string,
                public rule: Models.TagBaselineRule)
    {
        this.init();
    }

    get tagKeyName(): string
    {
        return extractDefinedTags(this.key);
    }

    get definedTagCaption(): string
    {
        if (this.inheritedText)
        {
            return this.inheritedText;
        }
        return this.key.startsWith("defined_tags.") ? "OCI Defined" : "";
    }

    private m_value: string;
    public get value(): string
    {
        return this.m_value;
    }

    chipValues: KeyValueChip[];

    public get inherited(): boolean
    {
        return this.type === TagType.Inherited;
    }

    public get aiEnhanced(): boolean
    {
        return this.type === TagType.AIEnhanced;
    }

    public get aiGenerated(): boolean
    {
        return this.type === TagType.AIGenerated;
    }

    public get inheritedText(): string
    {
        return this.inherited ? "Inherited" : "";
    }

    public static sortAndFilter(tags: TagRule[],
                                filter: string,
                                sorts: DatatableSort[] = []): TagRule[]
    {
        const result = [...tags];

        const sort = sorts[0];
        if (sort)
        {
            const ascending = sort.dir === "asc";
            switch (sort.prop)
            {
                case "key":
                    result.sort((a,
                                 b) => UtilsService.compareStrings(a.key, b.key, ascending));
                    break;

                case "value":
                    result.sort((a,
                                 b) => UtilsService.compareStrings(a.value, b.value, ascending));
                    break;
            }
        }

        if (filter)
        {
            filter = filter.toLocaleLowerCase();
            return result.filter((tag) =>
                                 {
                                     const key   = tag.key.toLocaleLowerCase();
                                     const value = tag.value.toLocaleLowerCase();
                                     return key.includes(filter) || value.includes(filter);
                                 });
        }
        else
        {
            return result;
        }
    }

    public static async getInheritedTags(scope: Models.TagBaselineScope,
                                         domain: AppDomainContext): Promise<Map<string, TagRule>>
    {
        const tags = new Map<string, TagRule>();
        if (!scope.inherit || scope instanceof Models.TagBaselineScopeGlobal)
        {
            return tags;
        }

        let definitions = await domain.tagBaselineDefinitions.getExtendedAll();
        definitions     = definitions.filter((d) => d.model.is_active);

        const relevantOrgLevel = await mapInParallelNoNulls(definitions, async (def: TagBaselineDefinitionExtended) =>
        {
            if (def.model.scope instanceof Models.TagBaselineScopeGlobal)
            {
                const version = await def.getRelease();
                return version.getDetails();
            }

            return null;
        });

        for (let org of relevantOrgLevel)
        {
            for (let key in org.tags)
            {
                tags.set(key, new TagRule(TagType.Inherited, key, org.tags[key]));
            }
        }

        if (scope instanceof Models.TagBaselineScopeCloudAccount)
        {
            let tenants = await domain.filterOptions.resolver.lookupTenantsByCloudAccounts(scope.cloud_account_ids);

            const relevantTenantLevel = await mapInParallelNoNulls(definitions, async (d: TagBaselineDefinitionExtended) =>
            {
                const scope = d.model.scope;
                if (scope instanceof Models.TagBaselineScopeTenant && tenants.length)
                {
                    if (scope.tenant_ids.some((tenant) => tenants.includes(tenant)))
                    {
                        const version = await d.getRelease();
                        return version.getDetails();
                    }
                }
                return null;
            });

            for (let tenant of relevantTenantLevel)
            {
                for (let key in tenant.tags)
                {
                    tags.set(key, new TagRule(TagType.Inherited, key, tenant.tags[key]));
                }
            }
        }
        return tags;
    }

    asAllowedValuesRule(): Models.TagBaselineRuleAllowedValues
    {
        if (this.rule instanceof Models.TagBaselineRuleAllowedValues) return this.rule;
        return null;
    }

    asConditionRule(): Models.TagBaselineRuleCondition
    {
        if (this.rule instanceof Models.TagBaselineRuleCondition) return this.rule;
        return null;
    }

    asRegexRule(): Models.TagBaselineRuleRegex
    {
        if (this.rule instanceof Models.TagBaselineRuleRegex) return this.rule;
        return null;
    }

    isValid(): boolean
    {
        const rule = this.rule;
        if (rule instanceof Models.TagBaselineRuleAllowedValues && !rule.allowed_values?.length) return false;
        if (rule instanceof Models.TagBaselineRuleCondition && !(rule.starts_with || rule.ends_with || rule.contains)) return false;
        if (rule instanceof Models.TagBaselineRuleRegex)
        {
            if (!rule.regex_pattern) return false;
            if (!UtilsService.isValidRegex(rule.regex_pattern)) return false;
        }

        return true;
    }

    private init()
    {
        this.chipValues = [];

        const rule = this.rule;
        if (rule instanceof Models.TagBaselineRuleCondition)
        {
            let results: string[] = [];
            if (rule.starts_with)
            {
                const key = "Starts With";
                results.push(`${key}: ${rule.starts_with}`);
                this.chipValues.push(new KeyValueChip(key, rule.starts_with, []));
            }
            if (rule.contains)
            {
                const key = "Contains";
                results.push(`${key}: ${rule.contains}`);
                this.chipValues.push(new KeyValueChip(key, rule.contains, []));
            }
            if (rule.ends_with)
            {
                const key = "Ends With";
                results.push(`${key}: ${rule.ends_with}`);
                this.chipValues.push(new KeyValueChip(key, rule.ends_with, []));
            }

            this.m_value = "Condition(s): " + results.join(", ");
        }
        else if (rule instanceof Models.TagBaselineRuleAllowedValues)
        {
            const key   = UtilsService.pluralize(rule.allowed_values.length, new PluralizedValues("Allowed Values", "Allowed Value", "Allowed Values"));
            const value = rule.allowed_values.join(", ");

            this.m_value = `${key}: ${value}`;
            this.chipValues.push(new KeyValueChip(key, value, rule.allowed_values));
        }
        else if (rule instanceof Models.TagBaselineRuleRegex)
        {
            const key    = "Regex";
            this.m_value = `${key}: ${rule.regex_pattern}`;
            this.chipValues.push(new KeyValueChip(key, rule.regex_pattern, []));
        }
    }
}
